import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getDownloadOutputList } from "../../reducer/download/Download.action";
import { showToaster } from "../../common/common";
import DownloadRequest from "../../services/DownloadRequest";
import { API_URL } from "../../config/Api_url";

const resolveDownloadUrl = (url) => {
  if (!url) return url;
  if (url.startsWith("http")) {
    const match = url.match(/\/api\/v1\/(.*)/); 
    if (match) {
      return `${API_URL}/${match[1]}`;
    }
  }
  if (url.startsWith("/")) {
    return `${API_URL}${url}`;
  }
  return url;
};

// A category scrape's output is a single category_asins.json (no paged ASIN
// files, no ZIP) — detect it so we can label/format the download appropriately.
const isCategoryRun = (run) =>
  (run?.download_links?.pages || []).some((p) =>
    String(p).endsWith("category_asins.json")
  );

const categoryPage = (run) =>
  (run?.download_links?.pages || []).find((p) =>
    String(p).endsWith("category_asins.json")
  );

const CATEGORY_CSV_COLS = [
  "asin", "parent_asin", "is_variant", "is_brand", "is_store",
  "is_prime", "seller_name", "price_cents", "in_stock",
];
const categoryRowsToCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [CATEGORY_CSV_COLS.join(",")];
  for (const r of rows || []) lines.push(CATEGORY_CSV_COLS.map((c) => esc(r[c])).join(","));
  return lines.join("\n");
};

const DownloadOutputTable = ({ collectionId }) => {
  const [output, setOutput] = useState([]);
  const [loader, setLoader] = useState(false);

  const dispatch = useDispatch();
  const _downloadOutputList = useSelector(
    (item) => item.downloadReducer.outPutList
  );

  const handleDownloadFile = async (page, index, isJSON = true) => {
    try {
      const option = {
        showLoader: "download-output-" + index,
      };

      const response = await DownloadRequest(page, {}, option);

      const mimeType = isJSON ? "application/json" : "application/zip";
      const blob = new Blob([response], { type: mimeType });

      const file_name = (page.split("/").pop() || "download").split("?")[0];
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = file_name;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error(error.message);
      showToaster(error.message, "error");
    }
  };

  // Category scrapes: convert the JSON product list to CSV client-side.
  const handleDownloadCSV = async (run, index) => {
    try {
      const option = { showLoader: "download-output-csv-" + index };
      const page = resolveDownloadUrl(categoryPage(run));
      const response = await DownloadRequest(page, {}, option);
      const text = new TextDecoder("utf-8").decode(response);
      const rows = JSON.parse(text);
      const blob = new Blob([categoryRowsToCsv(rows)], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `category_${collectionId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error(error.message);
      showToaster(error.message, "error");
    }
  };

  const loadOutputs = async (skip = 0) => {
    try {
      setLoader(true);
      const data = {
        collection_id: collectionId,
        limit: 50,
        skip: skip,
      };
      dispatch(getDownloadOutputList(data));
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoader(false);
    }
  };

  const loadMoreOutputs = async () => {
    const data = {
      collection_id: collectionId,
      limit: 50,
      skip: _downloadOutputList?.length,
    };
    try {
      setLoader(true);
      dispatch(getDownloadOutputList(data, true));
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadOutputs();
  }, [collectionId]);

  useEffect(() => {
    setOutput(_downloadOutputList);
  }, [_downloadOutputList]);

  // Format file size
  const formatFileSize = (sizeInBytes) => {
    if (!sizeInBytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let size = sizeInBytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Render empty state
  if (!loader && output.length === 0) {
    return (
      <div className="mca-table-wrap">
        <div className="mca-empty" style={{ padding: "56px 24px" }}>
          <div className="mca-empty__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="1"/>
              <path d="M12 1v6m0 6v6"/>
              <path d="M4.22 4.22l4.24 4.24m5.08 0l4.24-4.24"/>
              <path d="M1 12h6m6 0h6"/>
              <path d="M4.22 19.78l4.24-4.24m5.08 0l4.24 4.24"/>
            </svg>
          </div>
          <h3>No downloads yet</h3>
          <p>Run your collection to generate downloadable results.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mca-table-wrap">
      <table className="mca-table">
        <thead>
          <tr>
            <th>Run time</th>
            <th>Status</th>
            <th className="hide-md">Records</th>
            <th>Pages</th>
            <th style={{ textAlign: "right" }}>Archive</th>
          </tr>
        </thead>
        <tbody>
          {loader && output.length === 0 ? (
            <tr>
              <td colSpan="5" style={{ textAlign: "center", padding: "40px 16px" }}>
                <div className="mca-spinner" style={{ margin: "0 auto", width: "24px", height: "24px" }}></div>
                <p style={{ marginTop: "12px", color: "var(--mca-muted)", fontSize: "14px" }}>Loading downloads...</p>
              </td>
            </tr>
          ) : (
            output.map((run, runIdx) => (
              <tr key={runIdx}>
                <td className="cell-strong">
                  {moment(run.updated_at).format("LLL")}
                </td>
                <td>
                  {run.is_download_generated ? (
                    <span className="mca-badge mca-badge--soft-success">
                      <span className="dot"></span>
                      Completed
                    </span>
                  ) : (
                    <span className="mca-badge mca-badge--soft-info">
                      <span className="dot"></span>
                      Processing
                    </span>
                  )}
                </td>
                <td className="hide-md cell-muted">
                  {(
                    run.total_results ??
                    run.total_exacted_result ??
                    run.download_links?.pages?.length ??
                    0
                  ).toLocaleString()}
                </td>
                <td>
                  <div className="pages">
                    {!run.is_download_generated ? (
                      <span className="cell-muted">—</span>
                    ) : isCategoryRun(run) ? (
                      <>
                        <button
                          className="page-pill"
                          id={`download-output-${runIdx}`}
                          onClick={() =>
                            handleDownloadFile(resolveDownloadUrl(categoryPage(run)), runIdx, true)
                          }
                        >
                          Download full list (JSON)
                        </button>
                        <button
                          className="page-pill"
                          id={`download-output-csv-${runIdx}`}
                          onClick={() => handleDownloadCSV(run, runIdx)}
                        >
                          Download CSV
                        </button>
                      </>
                    ) : (
                      run.download_links?.pages?.map((page, pageIdx) => (
                        <button
                          key={pageIdx}
                          className="page-pill"
                          id={`download-output-${pageIdx}`}
                          onClick={() => handleDownloadFile(resolveDownloadUrl(page), pageIdx)}
                        >
                          Page {pageIdx + 1}
                        </button>
                      ))
                    )}
                  </div>
                </td>
                <td style={{ textAlign: "right" }}>
                  {run.is_download_generated && !isCategoryRun(run) ? (
                    <button
                      className="mca-btn mca-btn--outline mca-btn--sm"
                      id={`download-output-zip-${runIdx}`}
                      onClick={() =>
                        handleDownloadFile(
                          resolveDownloadUrl(run.download_links?.all_pages),
                          `zip-${runIdx}`,
                          false
                        )
                      }
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 3v12" />
                        <path d="m8 11 4 4 4-4" />
                        <path d="M4 19h16" />
                      </svg>
                      {run.download_links?.all_pages_size
                        ? `ZIP · ${formatFileSize(run.download_links.all_pages_size)}`
                        : "Download ZIP"}
                    </button>
                  ) : (
                    <span className="cell-muted">—</span>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="table-foot">
        <span>
          Showing {output.length} {output.length !== 1 ? "of" : "of"} {_downloadOutputList.length > 0 ? _downloadOutputList.length : output.length} runs
        </span>
        {_downloadOutputList.length > output.length && !loader && (
          <button className="mca-btn mca-btn--outline mca-btn--sm" onClick={loadMoreOutputs}>
            Load more
          </button>
        )}
      </div>
    </div>
  );
};

export default DownloadOutputTable;
